import { useEffect } from 'react';

/**
 * PageMeta — sets the document title + meta description (and OpenGraph variants)
 * on mount. Pure side-effect component; renders nothing.
 *
 * Usage:
 *   <PageMeta title="Pricing — RentKollect" description="..." />
 *
 * NOTE: We intentionally avoid react-helmet to keep the bundle lean.
 * This is safe for SPA SEO because:
 *  • Google Bot and Bing Bot execute JS and read the final document.title / meta
 *  • We also ship a reasonable default in public/index.html for non-JS crawlers
 */
const SITE_SUFFIX = 'RentKollect';
const DEFAULT_OG_IMAGE = '/og-image.png';

function setMetaTag(attr, key, content) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content || '');
}

function setCanonical(url) {
  if (typeof document === 'undefined' || !url) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

export default function PageMeta({
  title,
  description,
  suffix = true,
  ogImage,
  canonical,
  path,
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = suffix && title && !title.includes(SITE_SUFFIX)
      ? `${title} | ${SITE_SUFFIX}`
      : (title || SITE_SUFFIX);

    document.title = fullTitle;

    if (description) {
      setMetaTag('name', 'description', description);
      setMetaTag('property', 'og:description', description);
      setMetaTag('name', 'twitter:description', description);
    }

    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('name', 'twitter:title', fullTitle);
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:site_name', SITE_SUFFIX);

    const img = ogImage || DEFAULT_OG_IMAGE;
    const absImg = img.startsWith('http') ? img : (typeof window !== 'undefined' ? window.location.origin + img : img);
    setMetaTag('property', 'og:image', absImg);
    setMetaTag('property', 'og:image:width', '1200');
    setMetaTag('property', 'og:image:height', '630');
    setMetaTag('name', 'twitter:image', absImg);

    // Canonical: prefer explicit, else `path`, else current URL
    let canonicalUrl = canonical;
    if (!canonicalUrl && path && typeof window !== 'undefined') {
      canonicalUrl = window.location.origin + path;
    }
    if (!canonicalUrl && typeof window !== 'undefined') {
      canonicalUrl = window.location.origin + window.location.pathname;
    }
    if (canonicalUrl) {
      setCanonical(canonicalUrl);
      setMetaTag('property', 'og:url', canonicalUrl);
    }

    // Robots control (for auth-gated pages we can noindex)
    setMetaTag('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
  }, [title, description, suffix, ogImage, canonical, path, noindex]);

  return null;
}
